/**
 * usePortfolioNews - Fetches aggregated yFinance news for portfolio holdings.
 *
 * Calls GET /api/v1/portfolio/news on mount when positions exist.
 * Falls back to SPY/QQQ general market news if no portfolio.
 */

import { useState, useEffect } from 'react';
import { API_BASE_URL } from '@/lib/api';

export interface PortfolioNewsItem {
  title: string;
  url: string;
  ticker: string;
  publisher: string;
  published_date: string;
}

interface UsePortfolioNewsReturn {
  news: PortfolioNewsItem[];
  sourceTickers: string[];
  isLoading: boolean;
}

export function usePortfolioNews(hasPositions: boolean): UsePortfolioNewsReturn {
  const [news, setNews] = useState<PortfolioNewsItem[]>([]);
  const [sourceTickers, setSourceTickers] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!hasPositions) return;

    setIsLoading(true);
    const token = localStorage.getItem('access_token');
    fetch(`${API_BASE_URL}/api/v1/portfolio/news`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => {
        if (!r.ok) {
          console.error(`[usePortfolioNews] HTTP ${r.status}: ${r.statusText}`);
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (data) {
          setNews(data.news || []);
          setSourceTickers(data.source_tickers || []);
        }
      })
      .catch((err) => {
        console.error('[usePortfolioNews] Failed to fetch portfolio news:', err);
      })
      .finally(() => setIsLoading(false));
  }, [hasPositions]);

  return { news, sourceTickers, isLoading };
}
